import * as React from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid as MuiGrid, TextField} from '@mui/material';

export default function BookFormModal({
                                          open,
                                          onClose,
                                          initialValues = {},
                                          onSave,
                                          isSaving = false,
                                      }) {
    const isEdit = !!(initialValues && (initialValues.id ?? initialValues.book_id));

    const [form, setForm] = React.useState({
        id: null,
        book_id: '',
        title: '',
        author: '',
        isbn: '',
        edition: '',
        publisher: '',
        price: '',
        notes: '',
    });

    const [errors, setErrors] = React.useState({
        title: '',
        author: '',
        isbn: '',
        price: '',
    });

    React.useEffect(() => {
        if (!open) {
            setErrors({title: '', author: '', isbn: '', price: ''});
            return;
        }
        const row = initialValues || {};
        setForm({
            id: row.id ?? row.book_id ?? null,
            book_id: row.book_id ?? '',
            title: row.title ?? '',
            author: row.author ?? '',
            isbn: row.isbn ?? '',
            edition: row.edition ?? '',
            publisher: row.publisher ?? '',
            price: String(row.price ?? ''),
            notes: row.notes ?? ''
        });
    }, [open, initialValues]);

    const validateForm = () => {
        const newErrors = {
            title: '',
            author: '',
            isbn: '',
            price: ''
        };
        let isValid = true;

        // Required fields
        if (!form.title.trim()) {
            newErrors.title = 'Title is required';
            isValid = false;
        }
        if (!form.author.trim()) {
            newErrors.author = 'Author is required';
            isValid = false;
        }
        if (!form.isbn.trim()) {
            newErrors.isbn = 'ISBN is required';
            isValid = false;
        }
        if (!form.price.trim()) {
            newErrors.price = 'Price is required';
            isValid = false;
        }

        // ISBN format validation (basic ISBN-10 or ISBN-13 check)
        if (form.isbn.trim()) {
            const cleanIsbn = form.isbn.replace(/[-\s]/g, '');
            const isbn10Regex = /^[0-9]{9}[0-9X]$/;
            const isbn13Regex = /^[0-9]{13}$/;
            if (!isbn10Regex.test(cleanIsbn) && !isbn13Regex.test(cleanIsbn)) {
                newErrors.isbn = 'Required: Invalid ISBN format. Examples: ISBN-10 → 0-306-40615-2, ISBN-13 → 978-3-16-148410-0';
                isValid = false;
            }
        }

        // Price validation
        if (form.price.trim()) {
            const priceValue = parseFloat(form.price);
            if (isNaN(priceValue) || priceValue < 0) {
                newErrors.price = 'Price must be a positive number';
                isValid = false;
            } else if (!/^\d+(\.\d{1,2})?$/.test(form.price)) {
                newErrors.price = 'Price must have up to 2 decimal places';
                isValid = false;
            }
        }

        setErrors(newErrors);
        return isValid;
    };

    React.useEffect(() => {
        if (open) {
            validateForm();
        }
    }, [form, open]);

    const handleChange = (field) => (e) => {
        setForm((prev) => ({...prev, [field]: e.target.value}));
    };

    const handleSubmit = () => {
        if (!validateForm()) return;

        const idOrNull = form.id ?? null;
        const payload = {
            title: form.title.trim(),
            author: form.author.trim(),
            isbn: form.isbn.trim(),
            edition: form.edition.trim(),
            publisher: form.publisher.trim(),
            price: form.price.trim() ? parseFloat(form.price).toFixed(2) : '',
            notes: form.notes.trim()
        };
        onSave?.(idOrNull, payload);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEdit ? 'Edit Book' : 'Create Book'}</DialogTitle>
            <DialogContent dividers>
                <MuiGrid container spacing={2}>
                    <MuiGrid item xs={12} sm={6}>
                        <TextField
                            label="Title"
                            value={form.title}
                            onChange={handleChange('title')}
                            fullWidth
                            required
                            error={!!errors.title}
                            helperText={errors.title}
                        />
                    </MuiGrid>
                    <MuiGrid item xs={12} sm={6}>
                        <TextField
                            label="Author"
                            value={form.author}
                            onChange={handleChange('author')}
                            fullWidth
                            required
                            error={!!errors.author}
                            helperText={errors.author}
                        />
                    </MuiGrid>
                    <MuiGrid item xs={12} sm={6}>
                        <TextField
                            label="ISBN"
                            value={form.isbn}
                            onChange={handleChange('isbn')}
                            fullWidth
                            required
                            InputProps={{readOnly: isEdit ? true : false}}                            
                            error={!!errors.isbn}
                            helperText={errors.isbn}
                        />
                    </MuiGrid>
                    <MuiGrid item xs={12} sm={6}>
                        <TextField
                            label="Edition"
                            value={form.edition}
                            onChange={handleChange('edition')}
                            fullWidth
                        />
                    </MuiGrid>
                    <MuiGrid item xs={12} sm={6}>
                        <TextField
                            label="Publisher"
                            value={form.publisher}
                            onChange={handleChange('publisher')}
                            fullWidth
                        />
                    </MuiGrid>
                    <MuiGrid item xs={12} sm={6}>
                        <TextField
                            label="Price"
                            type="number"
                            value={form.price}
                            onChange={handleChange('price')}
                            fullWidth
                            required
                            inputProps={{step: '0.01'}}
                            error={!!errors.price}
                            helperText={errors.price}
                        />
                    </MuiGrid>
                    <MuiGrid item xs={12}>
                        <TextField
                            label="Notes"
                            value={form.notes}
                            onChange={handleChange('notes')}
                            fullWidth
                            multiline
                            minRows={2}
                        />
                    </MuiGrid>
                </MuiGrid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSaving || !!Object.values(errors).some(error => error)}
                >
                    {isSaving ? 'Saving…' : (isEdit ? 'Save' : 'Create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}